import { ForwardedRef, forwardRef, useCallback, useEffect, useState } from "react";
import { SelectInstance } from "react-select";

import TextboxStyles from "capitalrx-components-2.0/lib/forms/Textbox/styles.module.scss";
import { DataRow, LoadingGroup, Spinner } from "capitalrx-components-2.0/lib/general";

import { useId } from "../../hooks/useId";
import { Shelf } from "../../layout";
import { debounce } from "../../utilities";
import { getBasePropsFromHtmlProps } from "../../utilities/getBasePropsFromHtmlProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { useConsolidatedRows } from "../DataDropdown/useConsolidatedRows";
import { Dropdown } from "../Dropdown";
import { OptionItem } from "../Dropdown/OptionItem";
import { useInputDisabled, useInputReadonly } from "../InputGroup";
import { Placeholder } from "../Textbox/Placeholder";
import { InputContainer } from "../shared";
import { CustomSelect } from "./CustomSelect";
import { SearchTextboxProps } from "./SearchTextbox";

import styles from "./styles.module.scss";

/**
 * The `Input` component properties.
 */
export interface InputProps extends SearchTextboxProps<object> {
  /**
   * Indicates whether the input is required.
   */
  readonly required?: boolean;
}

export const Input = forwardRef(function Input(props: InputProps, ref: ForwardedRef<SelectInstance<OptionItem | null, boolean>>) {
  const [selectedRow, setSelectedRow] = useState<DataRow<object> | null>(null);
  const [inputValue, setInputValue] = useState("");

  const {
    dataSource,
    optionLabelProvider,
    defaultValue = null,
    searchField,
    suffix,
    onChange,
    label,
    "aria-label": ariaLabel,
    instructions,
    required,
    tooltip,
    hideInstructions,
    hideLabel,
    value = defaultValue,
  } = props;

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [TextboxStyles.Textbox, styles.ContainerContent, "textbox"],
    testIdPrefix: "search-textbox",
  });

  const id = useId();
  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);

  useEffect(() => {
    if (dataSource.pageSize < 10) dataSource.setPageSize(10);
  }, [dataSource.pageSize]);

  const rowState = useConsolidatedRows(dataSource);

  useEffect(() => {
    const doRowValueLoad = async () => {
      if (value !== null) {
        const row = selectedRow && selectedRow.id === value ? selectedRow : await dataSource.get(value);
        if (!row) onChange?.(null, value);
        else setSelectedRow((current) => (!current || current.id !== value ? row : current));
      } else setSelectedRow(null);
    };

    if (dataSource.busy) return;

    doRowValueLoad();
  }, [dataSource.get, dataSource.busy, value]);

  const searchChangeHandler = useCallback(
    debounce((value: string | null) => {
      if (searchField && !value) dataSource.filterBy(searchField, null);
      else if (searchField) dataSource.filterBy(searchField, value);
    }, 500),
    [dataSource, searchField],
  );

  const scrollHandler = useCallback(() => {
    if (dataSource.hasNextPage) dataSource.next();
  }, [dataSource]);

  const rowElements = rowState.rows.map(({ id, data }) => {
    return (
      <Dropdown.Option key={id} value={id}>
        {optionLabelProvider(data)}
      </Dropdown.Option>
    );
  });

  const items = selectedRow && value !== null && !rowState.indices[value] ? <Dropdown.Group>{rowElements}</Dropdown.Group> : rowElements;

  return (
    <LoadingGroup.Placeholder fallback={<Placeholder className={props.className} style={props.style} />}>
      <InputContainer
        {...getBasePropsFromHtmlProps(baseHtmlProps)}
        id={id}
        label={label}
        instructions={instructions}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        tooltip={tooltip}
        hideInstructions={hideInstructions}
        hideLabel={hideLabel}
      >
        <CustomSelect
          id={id}
          ref={ref}
          {...props}
          aria-label={ariaLabel}
          inputValue={inputValue}
          setInputValue={setInputValue}
          manualSearchFilter
          readOnly={props.readOnly}
          disabled={dataSource.status === "error" ? true : props.disabled}
          suffix={
            dataSource.busy ? (
              <Shelf>
                {suffix}
                <Spinner size="regular" />
              </Shelf>
            ) : (
              suffix
            )
          }
          onSearchChange={searchChangeHandler}
          onMenuScrollToBottom={scrollHandler}
        >
          {selectedRow && value !== null && !rowState.indices[value] && (
            <Dropdown.Option value={value}>{optionLabelProvider(selectedRow.data)}</Dropdown.Option>
          )}
          {items}
        </CustomSelect>
      </InputContainer>
    </LoadingGroup.Placeholder>
  );
});
